import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AvisoSinGrupoTarifa } from "@/components/AvisoSinGrupoTarifa";
import { PackageCheck, User, Minus, Plus, X } from "lucide-react";

const fmtColones = (n) => `₡ ${Number(n || 0).toLocaleString("es-CR")}`;

const FilaRepuesto = ({ r, testKey, onCant, onFila, onQuitar, onToggleIncluido }) => (
  <div data-testid={`plantilla-repuesto-fila-${testKey}`}
    className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-slate-200 p-2.5">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <p className={`text-sm font-medium truncate ${r.ya_incluido ? "text-slate-400 line-through" : "text-slate-700"}`}>{r.nombre}</p>
        {r.opcional && (
          <span data-testid={`plantilla-repuesto-opcional-badge-${testKey}`}
            className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
            Opcional
          </span>
        )}
        {r.ya_incluido && (
          <span data-testid={`plantilla-repuesto-incluido-badge-${testKey}`}
            className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            Ya incluido
          </span>
        )}
        {onToggleIncluido && (r.ya_incluido || r.tambien_en) && (
          <button type="button" data-testid={`plantilla-repuesto-toggle-incluido-${testKey}`}
            onClick={onToggleIncluido}
            className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
              r.ya_incluido ? "border-teal-300 text-teal-700 hover:bg-teal-50" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}>
            {r.ya_incluido ? "Incluir de todos modos" : "Marcar «Ya incluido»"}
          </button>
        )}
      </div>
      {!r.ya_incluido && r.tambien_en && (
        <p data-testid={`plantilla-repuesto-tambien-en-${testKey}`} className="text-[11px] text-slate-400 italic">
          También en {r.tambien_en}
        </p>
      )}
      {r.ya_incluido && r.incluido_en_kit && (
        <p data-testid={`plantilla-repuesto-incluido-kit-${testKey}`} className="text-[11px] text-slate-400 italic">
          Incluido en {r.incluido_en_kit}
        </p>
      )}
      {r.ya_incluido && !r.incluido_en_kit && r.maximo_excedido != null && (
        <p data-testid={`plantilla-repuesto-maximo-${testKey}`} className="text-[11px] text-slate-400 italic">
          Máximo {r.maximo_excedido} por trabajo
        </p>
      )}
      <p className="text-xs text-slate-400 font-mono">{r.codigo || "—"}</p>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-xs text-slate-400 mr-1">Cant.</span>
      <button data-testid={`plantilla-repuesto-menos-${testKey}`} onClick={() => onCant(-1)}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"><Minus size={13} /></button>
      <span data-testid={`plantilla-repuesto-cant-${testKey}`} className="w-6 text-center text-sm font-semibold tabular-nums">{r.cantidad}</span>
      <button data-testid={`plantilla-repuesto-mas-${testKey}`} onClick={() => onCant(1)}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"><Plus size={13} /></button>
    </div>
    <div className="flex shrink-0 rounded-lg border border-slate-200 overflow-hidden">
      <button type="button" data-testid={`plantilla-repuesto-traemos-${testKey}`}
        onClick={() => onFila({ aportado_cliente: false })}
        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${!r.aportado_cliente ? "bg-teal-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
        Lo traemos
      </button>
      <button type="button" data-testid={`plantilla-repuesto-cliente-${testKey}`}
        onClick={() => onFila({ aportado_cliente: true })}
        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${r.aportado_cliente ? "bg-amber-500 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
        Lo trae el cliente
      </button>
    </div>
    <button type="button" data-testid={`plantilla-repuesto-quitar-${testKey}`} title="No incluir este repuesto"
      onClick={onQuitar}
      className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 self-end sm:self-center">
      <X size={14} />
    </button>
  </div>
);

// Ventana de repuestos que trae la plantilla de un servicio.
// Por cada repuesto se decide: "Lo traemos" (lo suministra el taller, queda APROBADO)
// o "Lo trae el cliente" (informativo, sin compra ni aprobación).
// Modo agrupado (prop `grupos`): un grupo por el servicio principal + uno por cada plantilla
// generada por las tareas del cronograma (con FRT/mano de obra efectiva e "Incluida en X").
export function PlantillaRepuestosDialog({ open, onOpenChange, servicioNombre, items, grupos, onConfirmar, onAtras, sinGrupoTarifa }) {
  const agrupado = Array.isArray(grupos);
  const [sel, setSel] = useState([]);
  const [gruposSel, setGruposSel] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (agrupado) {
      setGruposSel((grupos || []).map((g) => ({ ...g,
        items: (g.items || []).map((r) => ({ ...r, ref_id: r.ref_id || r.repuesto_id, aportado_cliente: !!r.aportado_cliente })) })));
    } else {
      setSel((items || []).map((r) => ({
        ref_id: r.ref_id, codigo: r.codigo || "", nombre: r.nombre,
        categoria: r.categoria || "", cantidad: r.cantidad || 1,
        aportado_cliente: !!r.aportado_cliente, opcional: !!r.opcional,
        cantidad_maxima: r.cantidad_maxima ?? null,
      })));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Modo legado (flat, usado por TrabajoSolicitud.jsx) ----
  const setFila = (ref_id, patch) =>
    setSel((prev) => prev.map((r) => (r.ref_id === ref_id ? { ...r, ...patch } : r)));
  const setCant = (ref_id, delta) =>
    setSel((prev) => prev.map((r) => (r.ref_id === ref_id ? { ...r, cantidad: Math.max(1, (r.cantidad || 1) + delta) } : r)));
  const quitar = (ref_id) => setSel((prev) => prev.filter((r) => r.ref_id !== ref_id));
  const marcarTodos = (aportado_cliente) =>
    setSel((prev) => prev.map((r) => ({ ...r, aportado_cliente })));
  const nCliente = sel.filter((r) => r.aportado_cliente).length;
  const confirmar = () => { onConfirmar(sel); onOpenChange(false); };

  // ---- Modo agrupado (cronograma: servicio principal + generados) ----
  const setItemGrupo = (gi, ref_id, patch) =>
    setGruposSel((prev) => prev.map((g, j) => (j !== gi ? g
      : { ...g, items: g.items.map((r) => (r.ref_id === ref_id ? { ...r, ...patch } : r)) })));
  const setCantGrupo = (gi, ref_id, delta) =>
    setGruposSel((prev) => prev.map((g, j) => (j !== gi ? g
      : { ...g, items: g.items.map((r) => (r.ref_id === ref_id ? { ...r, cantidad: Math.max(1, (r.cantidad || 1) + delta) } : r)) })));
  const quitarItemGrupo = (gi, ref_id) =>
    setGruposSel((prev) => prev.map((g, j) => (j !== gi ? g : { ...g, items: g.items.filter((r) => r.ref_id !== ref_id) })));
  const toggleIncluidoGrupo = (gi, ref_id) =>
    setGruposSel((prev) => prev.map((g, j) => (j !== gi ? g
      : { ...g, items: g.items.map((r) => (r.ref_id === ref_id ? { ...r, ya_incluido: !r.ya_incluido } : r)) })));
  const quitarGrupo = (gi) => setGruposSel((prev) => prev.filter((_, j) => j !== gi));
  const marcarTodosGrupo = (gi, aportado_cliente) =>
    setGruposSel((prev) => prev.map((g, j) => (j !== gi ? g : { ...g, items: g.items.map((r) => ({ ...r, aportado_cliente })) })));
  const totalManoObra = gruposSel.reduce((a, g) => a + Number(g.mano_obra_efectiva ?? g.mano_obra ?? 0), 0);
  const totalRepuestosGrupos = gruposSel.reduce((a, g) => a + g.items.length, 0);
  const confirmarGrupos = () => { onConfirmar(gruposSel); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plantilla-repuestos-dialog" className="bg-white max-w-2xl max-h-[88vh] flex flex-col p-4 sm:p-6">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck size={18} className="text-indigo-600" /> Repuestos del servicio{servicioNombre ? ` — ${servicioNombre}` : ""}
          </DialogTitle>
          <DialogDescription>
            Indique quién aporta cada repuesto. Los que trae el cliente no se compran.
          </DialogDescription>
        </DialogHeader>

        {sinGrupoTarifa && <AvisoSinGrupoTarifa testId="plantilla-aviso-sin-grupo-tarifa" />}

        {!agrupado && (
          <div className="shrink-0 flex flex-wrap items-center gap-2 py-2 border-b border-slate-100">
            <span className="text-xs text-slate-500">Marcar todos:</span>
            <Button variant="outline" size="sm" data-testid="plantilla-repuestos-todos-traemos"
              onClick={() => marcarTodos(false)}
              className="h-7 border-teal-300 text-teal-700 hover:bg-teal-50">
              <PackageCheck size={13} className="mr-1" /> Lo traemos
            </Button>
            <Button variant="outline" size="sm" data-testid="plantilla-repuestos-todos-cliente"
              onClick={() => marcarTodos(true)}
              className="h-7 border-amber-300 text-amber-700 hover:bg-amber-50">
              <User size={13} className="mr-1" /> Lo trae el cliente
            </Button>
          </div>
        )}

        {!agrupado ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto mt-2 space-y-2">
              {sel.length === 0 && (
                <p data-testid="plantilla-repuestos-vacio" className="py-8 text-center text-sm text-slate-400">
                  Este servicio no tiene repuestos en su plantilla.
                </p>
              )}
              {sel.map((r) => (
                <FilaRepuesto key={r.ref_id} r={r} testKey={r.ref_id}
                  onCant={(d) => setCant(r.ref_id, d)} onFila={(p) => setFila(r.ref_id, p)} onQuitar={() => quitar(r.ref_id)} />
              ))}
            </div>
            <div className="shrink-0 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">
                {sel.length} repuesto{sel.length !== 1 ? "s" : ""}{nCliente > 0 ? ` · ${nCliente} los trae el cliente` : ""}
              </span>
              <div className="flex gap-2">
                {onAtras && (
                  <Button variant="outline" data-testid="plantilla-repuestos-atras-btn" onClick={() => { onConfirmar(sel); onAtras(); }}>
                    Atrás
                  </Button>
                )}
                <Button variant="outline" data-testid="plantilla-repuestos-cancelar-btn" onClick={() => onOpenChange(false)}>Cancelar</Button>
                <Button data-testid="plantilla-repuestos-confirmar-btn" onClick={confirmar}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Confirmar
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto mt-2 space-y-3">
              {gruposSel.length === 0 && (
                <p data-testid="plantilla-grupos-vacio" className="py-8 text-center text-sm text-slate-400">
                  Este servicio no tiene repuestos en su plantilla.
                </p>
              )}
              {gruposSel.map((g, gi) => (
                <div key={g.servicio_id} data-testid={`plantilla-grupo-${gi}`}
                  className="rounded-lg border border-slate-200 p-3 space-y-2.5 bg-slate-50/40">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">{g.nombre}</span>
                        {g.principal && (
                          <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-700">
                            Principal
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{g.frt ? `FRT ${g.frt}h` : "Sin FRT"}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {g.incluida_en ? (
                        <span data-testid={`plantilla-grupo-incluida-${gi}`}
                          className="rounded bg-teal-100 px-2 py-1 text-[11px] font-semibold text-teal-800 whitespace-nowrap">
                          Incluida en {g.incluida_en}
                        </span>
                      ) : (
                        <span data-testid={`plantilla-grupo-mano-obra-${gi}`} className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                          {fmtColones(g.mano_obra_efectiva ?? g.mano_obra)}
                        </span>
                      )}
                      {!g.principal && (
                        <button type="button" data-testid={`plantilla-grupo-quitar-${gi}`} title="Quitar este servicio generado"
                          onClick={() => quitarGrupo(gi)}
                          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  {g.items.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Marcar todos:</span>
                      <button type="button" data-testid={`plantilla-grupo-todos-traemos-${gi}`}
                        onClick={() => marcarTodosGrupo(gi, false)}
                        className="rounded border border-teal-300 px-2 py-0.5 text-[11px] font-semibold text-teal-700 hover:bg-teal-50">
                        Lo traemos
                      </button>
                      <button type="button" data-testid={`plantilla-grupo-todos-cliente-${gi}`}
                        onClick={() => marcarTodosGrupo(gi, true)}
                        className="rounded border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-50">
                        Lo trae el cliente
                      </button>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {g.items.map((r) => (
                      <FilaRepuesto key={r.ref_id} r={r} testKey={`${gi}-${r.ref_id}`}
                        onCant={(d) => setCantGrupo(gi, r.ref_id, d)}
                        onFila={(p) => setItemGrupo(gi, r.ref_id, p)}
                        onQuitar={() => quitarItemGrupo(gi, r.ref_id)}
                        onToggleIncluido={() => toggleIncluidoGrupo(gi, r.ref_id)} />
                    ))}
                    {g.items.length === 0 && (
                      <p className="text-xs text-slate-400 py-1 pl-1">Sin repuestos en este grupo.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="shrink-0 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-400" data-testid="plantilla-grupos-resumen">
                {totalRepuestosGrupos} repuesto{totalRepuestosGrupos !== 1 ? "s" : ""} · Total mano de obra:{" "}
                <b data-testid="plantilla-grupos-total-mo" className="text-slate-600">{fmtColones(totalManoObra)}</b>
              </span>
              <div className="flex gap-2">
                {onAtras && (
                  <Button variant="outline" data-testid="plantilla-repuestos-atras-btn" onClick={() => { onConfirmar(gruposSel); onAtras(); }}>
                    Atrás
                  </Button>
                )}
                <Button variant="outline" data-testid="plantilla-repuestos-cancelar-btn" onClick={() => onOpenChange(false)}>Cancelar</Button>
                <Button data-testid="plantilla-repuestos-confirmar-btn" onClick={confirmarGrupos}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Confirmar
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
